import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { lobbyAPI } from '../services/api';
import toast from 'react-hot-toast';

const formatShare = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '—';
  return `${v.toFixed(2)} MKD/h`;
};

const PlayLobbyPanel = () => {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const { data, isLoading } = useQuery('playLobbyMine', lobbyAPI.mine, {
    refetchInterval: 3000,
  });
  const lobby = data?.lobby || null;

  const invalidate = () => {
    queryClient.invalidateQueries('playLobbyMine');
    queryClient.invalidateQueries('gamingSessions');
    queryClient.invalidateQueries('userStats');
  };

  const fail = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    toast.error(typeof detail === 'string' ? detail : fallback);
  };

  const joinWait = useMutation(() => lobbyAPI.create({ product: 'wait' }), {
    onSuccess: invalidate,
    onError: (e) => fail(e, 'Could not join the wait lobby.'),
  });
  const makeParty = useMutation(() => lobbyAPI.create({ product: 'party' }), {
    onSuccess: invalidate,
    onError: (e) => fail(e, 'Could not create a party.'),
  });
  const joinParty = useMutation(
    () => lobbyAPI.create({ product: 'party', invite_code: code.trim() }),
    {
      onSuccess: invalidate,
      onError: (e) => fail(e, 'Could not join that party.'),
    }
  );
  const leave = useMutation(() => lobbyAPI.leave(lobby.id), {
    onSuccess: invalidate,
    onError: (e) => fail(e, 'Could not leave.'),
  });
  const vote = useMutation((n) => lobbyAPI.vote(lobby.id, n), {
    onSuccess: invalidate,
    onError: (e) => fail(e, 'Could not start.'),
  });

  const n = lobby?.filled_seats || 0;
  const voteLabel =
    n === 1
      ? 'Play solo 1:1 — new price from my wallet'
      : n === 2 || n === 3
        ? `Start with ${n} — we pay the full 4-seat GPU`
        : null;

  return (
    <div className="mt-4 mx-auto max-w-3xl text-left rounded-lg border border-violet-400/40 bg-violet-950/40 p-4 space-y-3">
      <p className="text-white font-semibold text-sm">Friends wait lobby</p>
      <p className="text-xs text-white/60">
        Strangers click in. Ghosts cannot be billed. After 5–15 min the people who
        are here pay the whole 4-seat box. Named RSVP with wallet holds is Party LAN
        on Profile — different product, different money.
      </p>
      {isLoading && <p className="text-white/50 text-sm">Loading lobby…</p>}
      {!lobby && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 rounded-lg text-sm"
            onClick={() => joinWait.mutate()}
            disabled={joinWait.isLoading}
          >
            Join wait queue
          </button>
          <button
            type="button"
            className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm"
            onClick={() => makeParty.mutate()}
            disabled={makeParty.isLoading}
          >
            Create party
          </button>
          <div className="flex gap-2 flex-1">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Party code"
              className="flex-1 px-3 py-2 rounded-md text-sm text-black"
            />
            <button
              type="button"
              className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm"
              onClick={() => joinParty.mutate()}
              disabled={joinParty.isLoading || !code.trim()}
            >
              Join party
            </button>
          </div>
        </div>
      )}
      {lobby && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm text-white/80">
            <span>
              {lobby.product} · {n}/{lobby.target_seats} seats · {lobby.status}
            </span>
            {lobby.invite_code ? (
              <span className="font-mono text-violet-200">code {lobby.invite_code}</span>
            ) : null}
          </div>
          <div className="h-2 rounded bg-white/10 overflow-hidden">
            <div
              className="h-full bg-violet-400"
              style={{ width: `${Math.min(100, (n / (lobby.target_seats || 4)) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-white/60">
            Timer {lobby.grace_elapsed ? 'done' : `${lobby.grace_seconds_left}s left`}.
            {lobby.machine_rate_mkd
              ? ` Full GPU ${formatShare(lobby.machine_rate_mkd)} · your share ${formatShare(lobby.your_share_mkd_per_hour)}.`
              : ' Rate is set when the VM starts.'}
            {lobby.gpu_type ? ` GPU ${lobby.gpu_type}.` : ''}
          </p>
          <ul className="text-sm text-white/80 space-y-1">
            {(lobby.members || []).map((m) => (
              <li key={m.user_id}>
                Seat {m.seat}: {m.username}
                {m.mine ? ' — you' : ''}
                {m.vote && m.vote !== 'none' ? ` · voted ${m.vote}` : ''}
              </li>
            ))}
          </ul>
          {lobby.can_vote && voteLabel && (
            <div className="space-y-2">
              <p className="text-xs text-amber-200">{lobby.warning}</p>
              <p className="text-xs text-white/50">
                Votes {lobby.votes_cast}/{lobby.votes_needed}. Everyone must click.
              </p>
              <button
                type="button"
                className="bg-amber-500 hover:bg-amber-600 text-black px-3 py-2 rounded-lg text-sm font-medium"
                onClick={() => vote.mutate(lobby.vote_start_with)}
                disabled={vote.isLoading}
              >
                {voteLabel}
              </button>
            </div>
          )}
          {lobby.status === 'open' && (
            <button
              type="button"
              className="text-sm text-white/50 underline"
              onClick={() => leave.mutate()}
              disabled={leave.isLoading}
            >
              Leave lobby
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayLobbyPanel;
